import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
type IconComponent = React.ComponentType<{ className?: string }>
import { AddCircle, Bill, Buildings2, Buildings3, Calendar, CalendarAdd, ChatSquare, ClockCircle, DollarMinimalistic, Help, Notebook, Settings, UsersGroupRounded, VolumeLoud, Wallet, Widget } from "@solar-icons/react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { ADMIN_HOME } from "@/constants/paths/admin"
import { useAuthStore } from "@/stores/auth"
import { hasAnyUserType } from "@/constants/user-types"
import { useBusinessOperationalAccess } from "@/hooks/use-business-operational-access"
import { isBusinessOperationalPath } from "@/lib/business-operational-access"
import { useTenantCapabilities } from "@/hooks/use-tenant-capabilities"

interface CommandAction {
  id: string
  label: string
  icon: IconComponent
  href: string
  keywords?: string[]
  group: "navigation" | "actions"
}

interface CommandPaletteContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null)

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext)
  if (!ctx) {
    throw new Error("useCommandPalette must be used within CommandPaletteProvider")
  }
  return ctx
}

export function CommandPaletteProvider({
  scope,
  children,
}: {
  scope: "business" | "admin"
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const user = useAuthStore((s) => s.user)
  const {
    canManageCampaigns,
    canAccessFinance,
    canManageProviders,
    canManageServices,
    canManageCustomers,
    canManageBusinesses,
    canAccessNavPath,
    isProviderOnly,
  } = useTenantCapabilities()
  const { canUseOperationalFeatures } = useBusinessOperationalAccess()

  const toggle = useCallback(() => setOpen((prev) => !prev), [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [])

  const actions = useMemo<CommandAction[]>(
    () =>
      scope === "admin"
        ? [
            { id: "admin-dashboard", label: t("commandPalette.admin.dashboard"), icon: Widget, href: ADMIN_HOME, group: "navigation" as const },
            { id: "admin-businesses", label: t("commandPalette.admin.businesses"), icon: Buildings2, href: `${ADMIN_HOME}/businesses`, group: "navigation" as const },
            { id: "admin-services", label: t("commandPalette.admin.services"), icon: ClockCircle, href: `${ADMIN_HOME}/services`, group: "navigation" as const },
            { id: "admin-providers", label: t("commandPalette.admin.providers"), icon: UsersGroupRounded, href: `${ADMIN_HOME}/providers`, group: "navigation" as const },
            { id: "admin-appointments", label: t("commandPalette.admin.appointments"), icon: Calendar, href: `${ADMIN_HOME}/appointments`, group: "navigation" as const },
            { id: "admin-customers", label: t("commandPalette.admin.customers"), icon: Notebook, href: `${ADMIN_HOME}/customers`, group: "navigation" as const },
            { id: "admin-transactions", label: t("commandPalette.admin.transactions"), icon: Bill, href: `${ADMIN_HOME}/transactions`, group: "navigation" as const },
            { id: "admin-payouts", label: t("commandPalette.admin.payouts"), icon: Wallet, href: `${ADMIN_HOME}/payouts`, group: "navigation" as const },
            { id: "admin-support", label: t("commandPalette.admin.reports"), icon: Help, href: `${ADMIN_HOME}/support`, group: "navigation" as const },
            { id: "admin-messaging", label: t("commandPalette.admin.messaging"), icon: ChatSquare, href: `${ADMIN_HOME}/messaging`, group: "navigation" as const },
            { id: "admin-ad-campaigns", label: t("commandPalette.admin.adCampaigns", { defaultValue: "Ad Campaigns" }), icon: VolumeLoud, href: `${ADMIN_HOME}/ad-campaigns`, group: "navigation" as const },
            { id: "admin-pending-ad-campaigns", label: t("commandPalette.admin.pendingAdCampaigns", { defaultValue: "Pending Ad Campaigns" }), icon: VolumeLoud, href: `${ADMIN_HOME}/pending-ad-campaigns`, group: "navigation" as const },
            { id: "admin-settings", label: t("commandPalette.admin.settings"), icon: Settings, href: `${ADMIN_HOME}/settings/profile`, group: "navigation" as const },
          ]
        : [
            { id: "dashboard", label: t("nav.dashboard"), icon: Widget, href: "/dashboard", group: "navigation" as const },
            { id: "appointments", label: t("nav.appointments"), icon: Calendar, href: "/appointments", group: "navigation" as const },
            { id: "slot-requests", label: t("nav.slotRequests"), icon: CalendarAdd, href: "/slot-requests", group: "navigation" as const },
            ...(canManageCustomers
              ? [{ id: "customers", label: t("nav.customers"), icon: Notebook, href: "/customers", group: "navigation" as const }]
              : []),
            ...(canManageServices
              ? [{ id: "services", label: t("nav.services"), icon: ClockCircle, href: "/services", group: "navigation" as const }]
              : []),
            ...(canManageProviders
              ? [{ id: "providers", label: t("nav.providers"), icon: UsersGroupRounded, href: "/providers", group: "navigation" as const }]
              : []),
            ...(canAccessFinance
              ? [{ id: "finance", label: t("nav.finance"), icon: DollarMinimalistic, href: "/finance", group: "navigation" as const }]
              : []),
            ...(canManageCampaigns
              ? [
                  { id: "support", label: t("nav.helpSupport"), icon: Help, href: "/support", group: "navigation" as const },
                  { id: "ad-campaigns", label: t("nav.adCampaigns"), icon: VolumeLoud, href: "/ad-campaigns", group: "navigation" as const },
                ]
              : []),
            ...(canManageBusinesses
              ? [{ id: "business", label: t("nav.businesses"), icon: Buildings2, href: "/business", group: "navigation" as const }]
              : []),
            { id: "settings", label: t("nav.settings"), icon: Settings, href: "/settings/profile", group: "navigation" as const },
            ...(canManageServices && !isProviderOnly
              ? [{
                  id: "create-service",
                  label: t("commandPalette.actions.createService"),
                  icon: AddCircle,
                  href: "/services/create",
                  keywords: [t("commandPalette.keywords.newService")],
                  group: "actions" as const,
                }]
              : []),
            ...(canManageProviders
              ? [{
                  id: "create-provider",
                  label: t("commandPalette.actions.createProvider"),
                  icon: AddCircle,
                  href: "/providers/create",
                  keywords: [t("commandPalette.keywords.newProvider")],
                  group: "actions" as const,
                }]
              : []),
            ...(canManageCampaigns
              ? [
                  {
                    id: "create-campaign",
                    label: t("commandPalette.actions.createCampaign"),
                    icon: AddCircle,
                    href: "/ad-campaigns/create",
                    group: "actions" as const,
                  },
                ]
              : []),
          ].filter((action) => canAccessNavPath(action.href)),
    [
      scope,
      t,
      canManageCampaigns,
      canAccessFinance,
      canManageProviders,
      canManageServices,
      canManageCustomers,
      canManageBusinesses,
      canAccessNavPath,
      isProviderOnly,
    ]
  )
  const availableActions = useMemo(
    () =>
      scope === "admin" || canUseOperationalFeatures
        ? actions
        : actions.filter((action) => !isBusinessOperationalPath(action.href)),
    [actions, canUseOperationalFeatures, scope]
  )
  const navigation = useMemo(
    () => availableActions.filter((a) => a.group === "navigation"),
    [availableActions]
  )
  const actionItems = useMemo(
    () => availableActions.filter((a) => a.group === "actions"),
    [availableActions]
  )

  const runAction = (href: string) => {
    setOpen(false)
    navigate(href)
  }

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen, toggle }}>
      {children}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={t("commandPalette.searchPlaceholder")} />
        <CommandList>
          <CommandEmpty>{t("commandPalette.empty")}</CommandEmpty>
          <CommandGroup heading={t("commandPalette.groups.navigation")}>
            {navigation.map((item) => (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.keywords?.join(" ") ?? ""}`}
                onSelect={() => runAction(item.href)}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
          {actionItems.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={t("commandPalette.groups.actions")}>
                {actionItems.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.keywords?.join(" ") ?? ""}`}
                    onSelect={() => runAction(item.href)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </CommandPaletteContext.Provider>
  )
}
